import React, { useEffect, useMemo } from 'react';
import { Link, useParams } from 'react-router-dom';
import { m } from 'framer-motion';
import Header from './Header';
import Footer from './Footer';
import CookieBanner from './CookieBanner';
import NotFoundPage from './NotFoundPage';
import { SITE_ORIGIN, internalUrl, tradesAreaUrl } from './siteConfig';
import { getTradesAreaBySlug } from './tradesAreasData';
import {
  DEFAULT_META_DESCRIPTION,
  DEFAULT_PAGE_TITLE,
  getMetaDescriptionEl,
  setCanonical
} from './seoDefaults';
import {
  constructionTradeKnowsAbout,
  isTopLandingTownSlug,
  tradesLocalBusinessAggregateRating
} from './localAreaPageSeo';
import { ORGANIZATION_SCHEMA_ID, organizationSchemaNode } from './organizationSchema';
import { tradesAreaClickyTitle, tradesAreaMetaDescription } from './tradesPageSeo';
import FreeSeoAuditCta from './FreeSeoAuditCta';

function variantIndex(slug) {
  let n = 0;
  for (let i = 0; i < slug.length; i += 1) n += slug.charCodeAt(i);
  return n % 3;
}

function leadParagraph(area) {
  const v = variantIndex(area.slug);
  const blocks = [
    `Plumbers, electricians, roofers and builders in ${area.name} win work when homeowners can see accreditations, areas covered, and clear calls to book a quote. WebCodeCraft builds fast, mobile-friendly sites with HTTPS, UK hosting options, and a pay-monthly model so you are not hit with a massive upfront web bill while you are already investing in tools and vans.`,
    `If you run a trade business in ${area.name}, ${area.region}, your website should do what word-of-mouth cannot at 9pm on a phone: show services, reviews, insurance and Gas Safe / NICEIC-style credentials where they matter. We deliver bespoke layouts and technical SEO baselines so phrases like "${area.name} builder" or "local ${area.name} plumber" have somewhere solid to land.`,
    `Contractors and specialist trades across ${area.region} use WebCodeCraft when they outgrow a Facebook-only presence. You get structured quote requests, maps and contact routes that work on-site, and ongoing patching and backups aligned with the tiers we publish—without paying for a full agency build up front.`
  ];
  return blocks[v];
}

function secondaryParagraph(area) {
  const v = variantIndex(`${area.slug}-b`);
  const blocks = [
    `Local competition on Checkatrade-style searches still sends people to your URL: if the site is slow or vague, you lose the job. We keep pages clear, accessibility-minded, and performance-conscious so trades in ${area.name} look as professional online as on the job.`,
    `Every published tier includes SSL, backup-minded hosting workflows, and security-oriented maintenance to the scope shown on our services page. Optional SEO retainers suit firms that want deeper reporting once the core trade site is live.`,
    `We work with sole traders and small teams in ${area.name} and the wider UK. Tell us your trade, service radius, and goals in the contact form—we reply with next steps and realistic delivery timing.`
  ];
  return blocks[v];
}

function thirdSectionParagraph(area) {
  const v = variantIndex(`${area.slug}-c`);
  const blocks = [
    `Whether you fit kitchens, lay driveways, or run emergency call-outs, your customers expect a site that loads on 4G beside the van. We design for thumb-friendly tap targets, readable prices or "from" bands, and trust blocks that match how trades actually sell.`,
    `Gas engineers, landscapers, and joinery firms near ${area.name} often need gallery-friendly layouts and seasonal campaign tweaks. Monthly care keeps your stack patched; larger redesigns or new service lines stay quoted separately so invoices stay predictable.`,
    `Link your Google Business Profile and social proof into the page story without clutter. We help you present reviews, guarantees, and coverage areas so homeowners searching in ${area.region} understand who you are in seconds.`
  ];
  return blocks[v];
}

function TradesAreaPage() {
  const { slug } = useParams();
  const area = getTradesAreaBySlug(slug);

  const canonical = area ? tradesAreaUrl(area.slug) : `${SITE_ORIGIN}/`;
  const schemaId = area ? `webcodecraft-trades-${area.slug}` : '';

  const jsonLd = useMemo(() => {
    if (!area) return null;
    const pageTitle = tradesAreaClickyTitle(area);
    const pageDesc = tradesAreaMetaDescription(area);
    return JSON.stringify({
      '@context': 'https://schema.org',
      '@graph': [
        organizationSchemaNode(),
        {
          '@type': 'WebPage',
          '@id': `${canonical}#webpage`,
          url: canonical,
          name: pageTitle,
          description: pageDesc,
          isPartOf: { '@type': 'WebSite', name: 'WebCodeCraft', url: SITE_ORIGIN },
          inLanguage: 'en-GB',
          ...(isTopLandingTownSlug(area.slug) ? { knowsAbout: constructionTradeKnowsAbout() } : {})
        },
        {
          '@type': 'BreadcrumbList',
          itemListElement: [
            { '@type': 'ListItem', position: 1, name: 'Home', item: SITE_ORIGIN },
            { '@type': 'ListItem', position: 2, name: 'Trades & builders', item: `${SITE_ORIGIN}/trades` },
            { '@type': 'ListItem', position: 3, name: area.name, item: canonical }
          ]
        },
        {
          '@type': 'LocalBusiness',
          '@id': `${canonical}#localbusiness`,
          name: `WebCodeCraft ${area.name}`,
          url: canonical,
          parentOrganization: { '@id': ORGANIZATION_SCHEMA_ID },
          aggregateRating: tradesLocalBusinessAggregateRating(`${canonical}#aggregateRating`),
          ...(isTopLandingTownSlug(area.slug) ? { knowsAbout: constructionTradeKnowsAbout() } : {}),
          areaServed: {
            '@type': 'Place',
            name: area.name,
            containedInPlace: {
              '@type': 'AdministrativeArea',
              name: area.region,
              containedInPlace: { '@type': 'Country', name: 'United Kingdom' }
            }
          }
        }
      ]
    });
  }, [area, canonical]);

  useEffect(() => {
    if (!area) return undefined;

    const title = tradesAreaClickyTitle(area);
    const description = tradesAreaMetaDescription(area);
    document.title = title;
    const meta = getMetaDescriptionEl();
    const prevDesc = meta?.getAttribute('content') || '';
    meta?.setAttribute('content', description);
    setCanonical(canonical);

    const existing = document.getElementById(schemaId);
    if (!existing && jsonLd) {
      const el = document.createElement('script');
      el.id = schemaId;
      el.type = 'application/ld+json';
      el.textContent = jsonLd;
      document.head.appendChild(el);
    }

    return () => {
      document.title = DEFAULT_PAGE_TITLE;
      meta?.setAttribute('content', prevDesc || DEFAULT_META_DESCRIPTION);
      setCanonical(`${SITE_ORIGIN}/`);
      const script = document.getElementById(schemaId);
      if (script) script.remove();
    };
  }, [area, canonical, jsonLd, schemaId]);

  if (!area) {
    return <NotFoundPage />;
  }

  return (
    <>
      <Header />
      <main
        id="main-content"
        className="pt-24 pb-16 min-w-0 overflow-x-clip bg-gradient-to-b from-gray-50 to-white min-h-screen"
      >
        <article className="container mx-auto px-4 max-w-3xl">
          <m.div
            initial={{ opacity: 0, y: 12 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.45 }}
          >
            <p className="text-sm text-indigo-600 font-medium mb-4">
              <Link to="/" className="hover:underline">
                Home
              </Link>
              <span className="mx-2 text-gray-400" aria-hidden="true">
                /
              </span>
              <Link to="/trades" className="hover:underline">
                Trades &amp; builders
              </Link>
              <span className="mx-2 text-gray-400" aria-hidden="true">
                /
              </span>
              <span className="text-gray-600">{area.name}</span>
            </p>

            {isTopLandingTownSlug(area.slug) && (
              <div className="mb-6 flex flex-col sm:flex-row flex-wrap gap-3">
                <a
                  href={internalUrl('contact')}
                  className="inline-flex w-full sm:w-auto min-h-[48px] flex-1 sm:flex-initial items-center justify-center px-6 py-3.5 rounded-xl bg-indigo-600 text-white font-semibold shadow-md hover:bg-indigo-700 transition-colors"
                >
                  Request a quote
                </a>
                <Link
                  to="/services"
                  className="inline-flex w-full sm:w-auto min-h-[48px] flex-1 sm:flex-initial items-center justify-center px-6 py-3.5 rounded-xl border-2 border-indigo-600 text-indigo-700 bg-white font-semibold shadow-sm hover:bg-indigo-50 transition-colors text-center"
                >
                  See pricing
                </Link>
              </div>
            )}

            <h1 className="text-3xl sm:text-4xl font-bold text-gray-900 mb-4 leading-tight">
              Web design &amp; development for trades in {area.name}, {area.region}
            </h1>
            <p className="text-lg font-medium text-indigo-800 mb-6 leading-snug">
              £0 upfront, low monthly hosting &amp; care—bespoke sites with custom code, not WordPress templates, for
              builders, plumbers, electricians and contractors in {area.name}.
            </p>

            <FreeSeoAuditCta className="mb-8" />

            <div className="space-y-5 text-gray-700 leading-relaxed">
              <p>{leadParagraph(area)}</p>
              <p>{secondaryParagraph(area)}</p>
            </div>

            <h2 className="text-xl font-bold text-gray-900 mt-10 mb-3">Why trades businesses choose a proper website</h2>
            <p className="text-gray-700 leading-relaxed mb-4">{thirdSectionParagraph(area)}</p>
            <ul className="list-disc pl-6 space-y-2 text-gray-700">
              <li>Mobile-first layouts for customers searching on phones after a leak or blown fuse</li>
              <li>Clear services, coverage map, and contact or quote routes</li>
              <li>HTTPS (SSL), UK-facing hosting, and backup-minded operations on published tiers</li>
              <li>Technical SEO foundations for local search in {area.name} and {area.region}</li>
            </ul>

            <h2 className="text-xl font-bold text-gray-900 mt-10 mb-3">Included on plans we publish</h2>
            <ul className="list-disc pl-6 space-y-2 text-gray-700">
              <li>Bespoke front-end design and custom builds—not generic WordPress themes</li>
              <li>Monthly hosting and care instead of a separate six-figure-style agency invoice up front</li>
              <li>Security patches and agreed support aligned with your tier</li>
            </ul>

            <p className="mt-8 text-gray-700 leading-relaxed">
              Compare packages on our{' '}
              <a href={internalUrl('services')} className="text-indigo-600 font-medium hover:underline">
                services &amp; pricing
              </a>{' '}
              page, review the{' '}
              <a href={internalUrl('faq')} className="text-indigo-600 font-medium hover:underline">
                FAQ
              </a>
              , or see{' '}
              <a href={internalUrl('portfolio')} className="text-indigo-600 font-medium hover:underline">
                recent work
              </a>
              . UK town and city pages:{' '}
              <Link to="/locations" className="text-indigo-600 font-medium hover:underline">
                locations hub
              </Link>
              .
            </p>

            <div className="mt-12 p-6 rounded-2xl bg-gradient-to-r from-indigo-600 to-cyan-600 text-white">
              <h3 className="text-xl font-bold mb-2">Get a quote from {area.name}</h3>
              <p className="mb-4 opacity-95">
                Tell us your trade and coverage area—we reply with honest fit and next steps.
              </p>
              <a
                href={internalUrl('contact')}
                className="inline-flex items-center justify-center px-6 py-3 bg-white text-indigo-600 font-semibold rounded-xl hover:bg-gray-100 transition-colors"
              >
                Contact WebCodeCraft
              </a>
            </div>

            <p className="mt-10 text-sm text-gray-500">
              <Link to="/trades" className="text-indigo-600 hover:underline">
                All trades &amp; builders city pages
              </Link>
            </p>
          </m.div>
        </article>
      </main>
      <Footer />
      <CookieBanner />
    </>
  );
}

export default TradesAreaPage;
